import React from 'react';
import { 
  ClipboardList, 
  Lightbulb, 
  TestTube, 
  Rocket, 
  Search, 
  TrendingUp,
  ArrowRight 
} from 'lucide-react';

const Process = () => {
  const processSteps = [
    {
      icon: <ClipboardList className="w-8 h-8 text-white" />,
      title: "Plan",
      description: "We analyze your requirements and create a comprehensive roadmap for your project.",
      color: "bg-blue-500"
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-white" />,
      title: "Create",
      description: "Our expert team develops innovative solutions tailored to your specific needs.",
      color: "bg-green-500"
    },
    {
      icon: <TestTube className="w-8 h-8 text-white" />,
      title: "Test",
      description: "Rigorous testing ensures quality, performance, and security of your solution.",
      color: "bg-purple-500"
    },
    {
      icon: <Rocket className="w-8 h-8 text-white" />,
      title: "Launch",
      description: "Smooth deployment and go-live with minimal disruption to your operations.",
      color: "bg-orange-500"
    },
    {
      icon: <Search className="w-8 h-8 text-white" />,
      title: "Review",
      description: "Continuous monitoring and evaluation to ensure optimal performance.",
      color: "bg-red-500"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-white" />,
      title: "Scale-Out",
      description: "Expand and optimize your solution as your business grows and evolves.",
      color: "bg-indigo-500"
    }
  ];

  return (
    <section id="process" className="section-padding bg-secondary-50">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            Our Process
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            We follow a proven methodology to ensure successful project delivery 
            from conception to scale-out.
          </p>
        </div>

        {/* Desktop Process Flow */}
        <div className="hidden lg:block">
          {/* Top Row */}
          <div className="flex justify-center items-center mb-16">
            {processSteps.slice(0, 3).map((step, index) => (
              <div key={index} className="flex items-center">
                {/* Step Card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 w-80 text-center">
                  <div className={`${step.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6`}>
                    {step.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-secondary-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                
                {/* Arrow */}
                {index < 2 && (
                  <ArrowRight className="w-8 h-8 text-secondary-400 mx-4" />
                )}
              </div>
            ))}
          </div>

          {/* Bottom Row */}
          <div className="flex justify-center items-center">
            {processSteps.slice(3, 6).map((step, index) => (
              <div key={index + 3} className="flex items-center">
                {/* Step Card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 w-80 text-center">
                  <div className={`${step.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6`}>
                    {step.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-secondary-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                
                {/* Arrow */}
                {index < 2 && (
                  <ArrowRight className="w-8 h-8 text-secondary-400 mx-4" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile/Tablet Process Flow */}
        <div className="lg:hidden grid md:grid-cols-2 gap-8">
          {processSteps.map((step, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className={`${step.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                {step.icon}
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">
                {step.title}
              </h3>
              <p className="text-secondary-600 text-sm leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Process Benefits */}
        <div className="mt-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Why Our Process Works
              </h3>
              <ul className="space-y-4 text-primary-100">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-200 rounded-full mt-3 flex-shrink-0"></div>
                  <span>Structured approach ensures nothing is overlooked</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-200 rounded-full mt-3 flex-shrink-0"></div>
                  <span>Continuous feedback and iteration throughout</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-200 rounded-full mt-3 flex-shrink-0"></div>
                  <span>Risk mitigation at every stage</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-200 rounded-full mt-3 flex-shrink-0"></div>
                  <span>Scalable solutions built from the ground up</span>
                </li>
              </ul>
            </div>
            <div className="text-center md:text-right">
              <div className="bg-white bg-opacity-20 rounded-2xl p-8 backdrop-blur-sm">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">6</div>
                <div className="text-primary-200 mb-4">Step Process</div>
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">100%</div>
                <div className="text-primary-200">Success Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
